import { useMemo } from "react";
import { configurations, PagesID } from "@utils/utilsConfigurations";
import {
  DynamicFilterField,
  DynamicFilterFieldType,
} from "@components/DynamicFilterCst/types";

export interface PageFilterField extends DynamicFilterField {
  onlyForType?: string[];

  accessorKey: string;
}

interface V1ColumnLike {
  accessorKey?: string;
  type?: string;
  optionsKey?: string;
  service?: string;
  optionsToDisable?: string;
  hasFilter?: boolean;
  visibility?: boolean;
  visibilityInTable?: boolean;
  permission?: string;
  labelHeader?: { en?: string; it?: string };
  onlyfor_type?: string[];
}

const V1_TYPE_TO_FILTER_TYPE: Record<string, DynamicFilterFieldType> = {
  text: "text",
  description: "text",
  autoIncrement: "text",
  association: "text",
  select: "select",
  "select-checkbox": "select",
  multiselect: "select",
  multiCheckbox: "multiselect",
  dateTime: "date",
  date: "date",
};

const hasColumnPermission = (
  column: V1ColumnLike,
  userPermissions: string[] | undefined,
): boolean => {
  if (!column.permission) return true;
  if (!userPermissions || userPermissions.length === 0) return false;
  const required = column.permission.split("&&");
  return userPermissions.some((p) => required.includes(p));
};

const mapV1ColumnToField = (
  column: V1ColumnLike,
  userPermissions: string[] | undefined,
): PageFilterField | null => {
  if (!column.hasFilter || !column.accessorKey || !column.type) return null;
  if (column.visibility === false) return null;
  if (column.visibilityInTable === false) return null;
  if (!hasColumnPermission(column, userPermissions)) return null;

  const filterType = V1_TYPE_TO_FILTER_TYPE[column.type];
  if (!filterType) return null;

  return {
    key: column.accessorKey,
    accessorKey: column.accessorKey,
    type: filterType,
    optionsKey: column.optionsKey,
    service: column.service,
    optionsToDisable: column.optionsToDisable,
    headerLabels: column.labelHeader,
    onlyForType: column.onlyfor_type,
  };
};

export const usePageFilters = (
  pageId: PagesID | undefined,
  userPermissions: string[] | undefined,
  excludeKeys: string[] = [],
): PageFilterField[] => {
  return useMemo(() => {
    if (!pageId) return [];
    const config = configurations[pageId] as { columns?: V1ColumnLike[] };
    if (!config?.columns) return [];

    return config.columns
      .map((column) => mapV1ColumnToField(column, userPermissions))
      .filter(
        (field): field is PageFilterField =>
          !!field && !excludeKeys.includes(field.key),
      );
  }, [pageId, userPermissions, excludeKeys]);
};
